import { deriveIntentForPlanningReport } from '../../../lib/generationImpact/selectedAssessmentIntent';
/**
 * Project Intent / Overview — narrative prose + property map with item-level layers.
 */
import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import type { SiteEntity, SiteLayer } from '../../../types/site';
import { resolveProjectIntentOverviewProse } from '../../../lib/buildProjectIntentOverview';
import { EditorialSectionShell } from './EditorialSectionShell';
import {
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyle, reportTypographySizes, REPORT_TEXT_PX } from '../../../lib/reportSectionHeading';
import { EditableHeading } from './EditableHeading';
import { PropertyMapContent } from './PropertyMapContent';
import { KeyFindingsEmptyState } from './KeyFindingsEmptyState';
import { ContextualKeyFindingsBlock } from './ContextualKeyFindingsBlock';
import { SectionQuarantineBanner } from './SectionQuarantineBanner';
import { walkClaimsText } from '../../../lib/walkClaimsText';
import { walkClaimText } from '../../../lib/walkClaimText';
import { NUMBERED_REF_TOKEN_RE, extractNumberedRefIds } from '../../../lib/citationTokens';
import { SavedScreeningProse } from '../overview/SavedScreeningProse';
import { filterRenderableParagraphs } from '../../../lib/safeNarrativeProse';
import { EditorialDatasheet, type DatasheetRow } from './editorial';
import { EditorialProseBody } from './editorial/EditorialProseBody';
import { hasGeneratedEditorialLayer } from '../../../lib/editorial/resolveEditorialLayer';
import { TIMELINE_BUCKET_LABEL, effectivePower, effectivePhases, formatInServiceDate } from '../../../lib/projectIntent';
import { effectiveWorkloadProfile, getWorkloadProfile } from '../../../lib/workloadProfile';
import { roleColor } from '../../../lib/reportRoleColors';
import { isScreeningRecommendationMode } from '../../../lib/intent/screeningIntentMode';
import {
  derivePresentedScreeningRecommendationSetV1,
  deriveScreeningRecommendationsForPresentation,
  presentScreeningRecommendationFields,
} from '../../../lib/findings/screeningRecommendationsForPresentation';
import type { ProjectIntent, RagCategory, RagStatus } from '../../../types/siteRag';

export interface ProjectIntentOverviewSectionProps {
  report: SiteRagReport;
  theme: ReportTheme;
  reportSite?: SiteEntity;
  reportSiteLayers: SiteLayer[];
  propertyMapSurfaceMode: 'interactive' | 'static';
  onEditLayers?: () => void;
  isAdmin?: boolean;
  onSectionRegen?: (sectionId: string) => Promise<void> | void;
  isEditing?: boolean;
  headingOverrides?: Record<string, string>;
  onHeadingChange?: (key: string, value: string) => void;
  sectionDefaultHeading: (key: string, fallback: string) => string;
  sectionStyle: React.CSSProperties;
  sectionPageColumnProps?: Record<string, unknown>;
  shellPadding?: boolean;
  omitSectionSidebar?: boolean;
  extraClassName?: string;
  exportMode?: boolean;
  /** RC4 — the un-merged theme-preset id, forwarded to the site-map figure so
   *  Monograph framing is selected by preset id, not the heading-font sniff. */
  presetId?: string | null;
  /** Part L2 — when true (VITE_EDITORIAL_LAYOUTS on), prepend the editorial
   *  hero-numeral opener above the heading so this inline section matches the
   *  router-rendered ones. Default false ⇒ byte-stable (nothing added). */
  editorialActive?: boolean;
  /** Part L2 — the 1-based section index for the hero numeral (from the same
   *  sectionDisplayNumbers the TOC + router consume). Out-of-range ⇒ no numeral. */
  editorialSectionNumber?: number;
}

function resolveOverviewProse(report: SiteRagReport): string | null {
  return resolveProjectIntentOverviewProse({ ...report, projectIntentAtGen: deriveIntentForPlanningReport(report, report.projectIntentAtGen) });
}

/** Title-case a coolingStrategy / nuclearInterest enum into a clean label. */
function titleizeEnum(value: string | undefined | null): string | null {
  if (!value || value === 'undecided' || value === 'undefined') return null;
  return value
    .split(/[-_\s]+/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function hasDeclaredProposalFields(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  const power = effectivePower(intent);
  if (power.bucket !== 'undecided') return true;
  if (titleizeEnum(intent.coolingStrategy) != null) return true;
  if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering' || intent.nuclearInterest === 'no') return true;
  const phased = effectivePhases(intent);
  if (phased.isPhased && phased.phaseCount > 1) return true;
  if (phased.initialInServiceDate || intent.inServiceDate) return true;
  return false;
}

/**
 * Resolve the effective screening structure mode for a report:
 * - Explicit `intent.screeningMode` ('recommend' | 'evaluate-proposal') always wins.
 * - Legacy reports without `screeningMode` default to 'evaluate-proposal' when
 *   concrete proposal targets were declared, or 'recommend' when left open.
 */
export function resolveReportScreeningMode(
  report: SiteRagReport,
  intent?: ProjectIntent,
): 'recommend' | 'evaluate-proposal' {
  const effectiveIntent = intent ?? deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  if ( isScreeningRecommendationMode(effectiveIntent) ) return 'recommend';
  if (effectiveIntent?.screeningMode === 'evaluate-proposal') return 'evaluate-proposal';
  if (hasDeclaredProposalFields(effectiveIntent)) return 'evaluate-proposal';
  return 'recommend';
}

/**
 * SECTION-PORT (3B) — resolve the "at a glance" spec rows from the gen-time
 * intent snapshot + site geometry. Each row is dropped when its source value
 * is absent (EditorialDatasheet renders only renderable values), so a thin
 * report shows a shorter ledger — never a fabricated spec. All figures are
 * read through the canonical resolvers (effectivePower / effectivePhases) the
 * renderer already uses elsewhere.
 *
 * J5 — emitted as compact dotted-leader datasheet rows (key … value) instead
 * of the taller stacked EditorialDefinitionList, so the Overview map + brief
 * fit on ONE page.
 */
function resolveIntentSpecRail(report: SiteRagReport): DatasheetRow[] {
  const items: DatasheetRow[] = [];
  const intent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  const recs = deriveScreeningRecommendationsForPresentation(report, report.siteId);
  if (intent) {
    if (intent.screeningMode === 'recommend') {
      items.push({ key: 'Screening Mode', value: 'Recommend an approach' });
    } else if (intent.screeningMode === 'evaluate-proposal') {
      items.push({ key: 'Screening Mode', value: 'Evaluate my proposal' });
    }
    const phased = effectivePhases(intent);
    const power = effectivePower(intent);
    const phasedUltimateTbdWithFloor = phased.isPhased
      && phased.totalCapacityMW == null
      && phased.ultimateCapacityBucket == null
      && phased.knownCapacityFloorMW != null;
    if (phasedUltimateTbdWithFloor) {
      items.push({
        key: 'Target Power',
        value: `Ultimate TBD (${phased.knownCapacityFloorMW} MW known floor)`,
      });
    } else if (power.bucket !== 'undecided') {
      items.push({ key: 'Target Power', value: power.displayLabel });
    } else if (intent.screeningMode === 'recommend' && recs['dc-power']?.value) {
      items.push({ key: 'Recommended Load Range', value: recs['dc-power'].value });
    }
    const profile = effectiveWorkloadProfile(intent);
    const profileLabel = getWorkloadProfile(profile).label;
    if (profileLabel) {
      items.push({ key: 'Workload', value: profileLabel });
    }
    const cooling = titleizeEnum(intent.coolingStrategy);
    if (cooling) {
      items.push({ key: 'Cooling', value: cooling });
    } else if (intent.screeningMode === 'recommend' && recs['cooling-strategy']?.value) {
      items.push({ key: 'Recommended Cooling', value: recs['cooling-strategy'].value });
    }
    if (intent.screeningMode === 'recommend' && recs['btm-architecture']?.value) {
      items.push({ key: 'Recommended Power Path', value: recs['btm-architecture'].value });
    }
    if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') {
      items.push({
        key: 'Nuclear Interest',
        value: intent.nuclearInterest === 'primary' ? 'Primary path' : 'Under consideration',
      });
    } else if (intent.nuclearInterest === 'no') {
      items.push({
        key: 'Nuclear Interest',
        value: 'Excluded',
      });
    }
    if (phased.isPhased && phased.phaseCount > 1) {
      items.push({ key: 'Phasing', value: `${phased.phaseCount} phases` });
    }
    // First COD (initial in-service date) when a real date exists.
    const cod = phased.initialInServiceDate ?? intent.inServiceDate ?? null;
    if (cod) {
      if (phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate && phased.ultimateCodTimeline) {
        const finalPhaseLabel = phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
        items.push({ key: 'Phase 1 COD', value: formatInServiceDate(cod) });
        items.push({
          key: `${finalPhaseLabel} Target`,
          value: `${TIMELINE_BUCKET_LABEL[phased.ultimateCodTimeline]} (planning target, not a COD)`,
        });
      } else if (phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate) {
        const finalPhaseLabel = phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
        items.push({ key: 'Phase 1 COD', value: formatInServiceDate(cod) });
        items.push({
          key: `${finalPhaseLabel} COD`,
          value: 'TBD',
        });
      } else {
        items.push({ key: 'First COD', value: formatInServiceDate(cod) });
      }
    }
  }
  // Site geometry — prefer the canonical boundary aggregate, fall back to the
  // legacy siteArea acres. Drop entirely when neither is present.
  const acres = report.siteBoundaryAggregate?.totalArea ?? report.siteArea ?? null;
  if (typeof acres === 'number' && Number.isFinite(acres) && acres > 0) {
    items.push({
      key: 'Site Area',
      value: `${acres.toLocaleString('en-US', { maximumFractionDigits: 0 })} acres`,
    });
  }
  return items;
}

type LooseOverviewReport = SiteRagReport & {
  scores?: Array<{ category: string; status?: RagStatus; detail?: string; headline?: string }>;
  gridAnalysis?: {
    availableCapacity?: string;
    nearestSubstation?: string;
    interconnectionTimeline?: string;
  };
  coolingAnalysis?: {
    recommendedSystem?: string;
    waterAvailability?: string;
  };
  btmViability?: {
    recommendedPath?: string;
    summary?: string;
  };
};

function getReportCategoryList(report: SiteRagReport): Array<
  RagCategory & {
    category?: string;
    name?: string;
    summary?: string;
    metrics?: Array<{
      id?: string;
      name?: string;
      label?: string;
      value?: string | number | null;
      source?: string;
      missing?: boolean;
    }>;
  }
> {
  return (report.categories ?? (report.conclusion as { categories?: unknown })?.categories ?? []) as never;
}

function findCategoryScore(report: SiteRagReport, pattern: RegExp): { status?: RagStatus; detail?: string } | undefined {
  const loose = report as LooseOverviewReport;
  const scoreMatch = (loose.scores ?? []).find((s) => pattern.test(s.category));
  if (scoreMatch) {
    return { status: scoreMatch.status, detail: scoreMatch.detail || scoreMatch.headline };
  }
  const catMatch = getReportCategoryList(report).find((c) => {
    return pattern.test(c.category ?? c.name ?? c.label ?? c.id ?? '');
  });
  if (catMatch) {
    const looseCat = catMatch as Omit<RagCategory, 'status'> & {
      category?: string;
      summary?: string;
      status?: RagStatus | 'withheld';
    };
    const status = looseCat.status === 'withheld' ? undefined : catMatch.status;
    return { status, detail: looseCat.summary ?? catMatch.explanation };
  }
  return undefined;
}

function alignmentBadgeFromStatus(status: RagStatus | 'withheld' | undefined): { label: string; className: string } {
  if (status === 'green') {
    return { label: 'PASS', className: 'bg-emerald-500/15 text-emerald-800 border-emerald-500/30' };
  }
  if (status === 'red') {
    return { label: 'CONSTRAINT / HOLD', className: 'bg-rose-500/15 text-rose-800 border-rose-500/30' };
  }
  if (status === 'amber') {
    return { label: 'CONDITIONAL', className: 'bg-amber-500/15 text-amber-800 border-amber-500/30' };
  }
  return { label: 'EVALUATED', className: 'bg-slate-500/10 text-slate-700 border-slate-500/25' };
}

interface ProposalAlignmentRow {
  dimension: string;
  declaredProposal: string;
  verifiedEvidence: string;
  badge: { label: string; className: string };
}

function buildProposalAlignmentRows(report: SiteRagReport, intent: ProjectIntent | undefined): ProposalAlignmentRow[] {
  const loose = report as LooseOverviewReport;
  const rows: ProposalAlignmentRow[] = [];
  const power = intent ? effectivePower(intent) : { bucket: 'undecided' as const, displayLabel: 'Undecided' };
  const powerScore = findCategoryScore(report, /power|grid|energy/i);
  const hasExplicitMw = typeof intent?.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW);
  const rawBasis = intent?.powerBasis as string | undefined;
  const basisLabel = rawBasis === 'it-load'
    ? (hasExplicitMw ? 'Recorded IT load input' : 'IT basis; exact MW unconfirmed')
    : rawBasis === 'facility' || rawBasis === 'total-facility'
      ? (hasExplicitMw ? 'Recorded facility demand input' : 'Default/preliminary facility basis; confirmation pending')
      : 'Basis unconfirmed; confirmation pending';

  const phased = intent ? effectivePhases(intent) : {
    isPhased: false,
    phaseCount: 0,
    phases: [],
    totalCapacityMW: null,
    ultimateCapacityBucket: null,
    knownCapacityFloorMW: null,
    initialInServiceDate: null,
    ultimateInServiceDate: null,
    ultimateCodTimeline: null,
    lastKnownInServiceDate: null,
  };
  const phasedUltimateTbdWithFloor = phased.isPhased
    && phased.totalCapacityMW == null
    && phased.ultimateCapacityBucket == null
    && phased.knownCapacityFloorMW != null;
  if (power.bucket !== 'undecided' || phasedUltimateTbdWithFloor || loose.gridAnalysis) {
    const gridEvidenceParts = [
      loose.gridAnalysis?.availableCapacity ? `Capacity: ${loose.gridAnalysis.availableCapacity}` : null,
      loose.gridAnalysis?.nearestSubstation ? `POI: ${loose.gridAnalysis.nearestSubstation}` : null,
      powerScore?.detail ?? null,
    ].filter(Boolean);
    const gridCat = getReportCategoryList(report).find((c) =>
      /power|grid|energy/i.test(c.category ?? c.name ?? c.label ?? c.id ?? ''),
    );
    const isPlanningDefaultOnlyGrid = Boolean(
      gridCat?.metrics?.some(
        (m) => {
          const looseMetric = m as { source?: string; name?: string; label?: string; value?: string };
          return (
            looseMetric.source === 'planning-default' ||
            /planning-default(?:\s+elcc)?/i.test(`${looseMetric.name ?? looseMetric.label ?? ''} ${looseMetric.value ?? ''}`)
          );
        },
      ) || /planning-default(?:\s+elcc)?/i.test(powerScore?.detail ?? ''),
    );
    const rawPowerStatus = powerScore?.status ?? report.overallStatus;
    const effectivePowerStatus =
      rawPowerStatus === 'green' && isPlanningDefaultOnlyGrid ? 'amber' : rawPowerStatus;
    const baseGridEvidence = gridEvidenceParts[0] ?? 'Subject to utility capacity & POI verification';
    const verifiedGridEvidence =
      isPlanningDefaultOnlyGrid && !/utility headroom/i.test(baseGridEvidence)
        ? `${baseGridEvidence} (planning-default ELCC model — utility headroom & station voltage unverified)`
        : baseGridEvidence;
    rows.push({
      dimension: 'Target Capacity & Basis',
      declaredProposal: phasedUltimateTbdWithFloor
        ? `Ultimate capacity TBD (${phased.knownCapacityFloorMW} MW known floor; ${basisLabel})`
        : power.bucket !== 'undecided'
          ? `${power.displayLabel} (${basisLabel})`
          : 'Open / Not specified',
      verifiedEvidence: verifiedGridEvidence,
      badge: alignmentBadgeFromStatus(effectivePowerStatus),
    });
  }

  const cod = phased.initialInServiceDate ?? intent?.inServiceDate ?? null;
  const timelineLabel = titleizeEnum(intent?.timeline);
  const pathwayTimeframes = (report.developmentPathway ?? [])
    .map((p) => {
      const lp = p as { phase?: string; timeframe?: string; cod?: string };
      const tf = lp.timeframe ?? lp.cod;
      if (!tf) return null;
      return lp.phase && (report.developmentPathway?.length ?? 0) > 1 ? `${lp.phase}: ${tf}` : tf;
    })
    .filter((v): v is string => Boolean(v));
  const pathwayTimeframe = pathwayTimeframes.length > 0 ? pathwayTimeframes.join(' · ') : undefined;
  if ((phased.isPhased && phased.phaseCount > 1) || cod || timelineLabel || loose.gridAnalysis?.interconnectionTimeline) {
    const finalPhaseLabel = phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
    const schedulePart = cod
      ? (phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate && phased.ultimateCodTimeline
        ? `Phase 1 COD ${formatInServiceDate(cod)} · ${finalPhaseLabel}: ${TIMELINE_BUCKET_LABEL[phased.ultimateCodTimeline]} (planning target, not a COD)`
        : phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate
          ? `Phase 1 COD ${formatInServiceDate(cod)} · ${finalPhaseLabel} COD TBD`
          : `First COD ${formatInServiceDate(cod)}`)
      : timelineLabel;
    const declaredParts = [
      phased.isPhased && phased.phaseCount > 1 ? `${phased.phaseCount} phases (requested schedule; basis confirmation pending)` : null,
      schedulePart,
    ].filter(Boolean);
    rows.push({
      dimension: 'Delivery Schedule & Phasing',
      declaredProposal: declaredParts.join(' · ') || 'Flexible timeline',
      verifiedEvidence:
        loose.gridAnalysis?.interconnectionTimeline
        || pathwayTimeframe
        || 'Subject to utility interconnection queue & study schedule',
      badge: alignmentBadgeFromStatus(powerScore?.status),
    });
  }

  const coolingLabel = titleizeEnum(intent?.coolingStrategy);
  const heatRejectionLabel = titleizeEnum(intent?.bopHeatRejection);
  const coolingScore = findCategoryScore(report, /cooling|water|climate|thermal/i);
  if (coolingLabel || heatRejectionLabel || loose.coolingAnalysis) {
    const coolingEvidenceParts = [
      loose.coolingAnalysis?.recommendedSystem ? `Screen: ${loose.coolingAnalysis.recommendedSystem}` : null,
      loose.coolingAnalysis?.waterAvailability ? `Water: ${loose.coolingAnalysis.waterAvailability}` : null,
      coolingScore?.detail ?? null,
    ].filter(Boolean);
    const prelimCooling = [coolingLabel, heatRejectionLabel].filter(Boolean).join(' · ');
    rows.push({
      dimension: 'Cooling & Heat Rejection',
      declaredProposal: prelimCooling
        ? `${prelimCooling} (recorded preliminary input; confirmation pending)`
        : 'Open cooling strategy',
      verifiedEvidence: coolingEvidenceParts[0] ?? 'Subject to site design wet-bulb & water supply verification',
      badge: alignmentBadgeFromStatus(coolingScore?.status),
    });
  }

  const nuclearStance = intent?.nuclearInterest;
  if (nuclearStance && nuclearStance !== 'undecided') {
    const proposalLabel = nuclearStance === 'no'
      ? 'Nuclear / SMR excluded'
      : nuclearStance === 'primary'
        ? 'Nuclear as primary pathway (preliminary input)'
        : 'Nuclear under consideration (preliminary input)';
    rows.push({
      dimension: 'On-Site Generation & Nuclear Posture',
      declaredProposal: proposalLabel,
      verifiedEvidence:
        loose.btmViability?.summary
        || (nuclearStance === 'no'
          ? 'Non-nuclear grid/BTM pathways evaluated per brief constraint'
          : 'Evaluated against site BTM & licensing feasibility criteria'),
      badge: alignmentBadgeFromStatus(powerScore?.status),
    });
  }

  const acres = report.siteBoundaryAggregate?.totalArea ?? report.siteArea ?? null;
  const landScore = findCategoryScore(report, /land|site|environmental|flood|acre/i);
  if ((typeof acres === 'number' && Number.isFinite(acres) && acres > 0) || landScore) {
    const landCat = getReportCategoryList(report).find((c) =>
      /land|site|environmental|flood|acre/i.test(c.category ?? c.name ?? c.label ?? c.id ?? ''),
    );
    const hasMissingLandMetrics = Boolean(
      landCat?.metrics?.some((m) => m.missing || /unassessed|unknown|not specified|evidence gap/i.test(String(m.value ?? ''))) ||
      /evidence gap\s*[—-]\s*excluded from scoring|not specified/i.test(landScore?.detail ?? ''),
    );
    const effectiveLandStatus =
      landScore?.status === 'green' && hasMissingLandMetrics ? 'amber' : landScore?.status;
    const baseLandEvidence =
      landScore?.detail
      || (typeof acres === 'number' && acres > 0
        ? `${acres.toLocaleString('en-US', { maximumFractionDigits: 2 })} gross acres screened; usable/buildable acreage requires flood/wetland/setback exclusions`
        : 'Buildable net acreage subject to parcel exclusion mapping');
    const verifiedLandEvidence =
      hasMissingLandMetrics && !/net buildable area & flood exposure unassessed/i.test(baseLandEvidence)
        ? `${baseLandEvidence} (net buildable area & flood exposure unassessed)`
        : baseLandEvidence;
    rows.push({
      dimension: 'Site Envelope & Buildable Land',
      declaredProposal: typeof acres === 'number' && acres > 0
        ? `${acres.toLocaleString('en-US', { maximumFractionDigits: 2 })} gross acres selected study area (not net buildable)`
        : 'Study area boundary',
      verifiedEvidence: verifiedLandEvidence,
      badge: alignmentBadgeFromStatus(effectiveLandStatus),
    });
  }

  return rows;
}

function overviewParagraphs(overview: string): string[] {
  return filterRenderableParagraphs(overview).split(/\n\n+/).filter(Boolean);
}

/** Resolve known citations through the full report's appendix. Preserve an
 * unmatched saved marker visibly; a layout repair must not erase provenance. */
function renderSavedRecommendationText(value: string, report: SiteRagReport): React.ReactNode {
  const splitter = new RegExp(`(${NUMBERED_REF_TOKEN_RE.source})`, 'gi');
  return value.split(splitter).map((part, index) => {
    const ids = extractNumberedRefIds(part);
    const resolvable = ids.length > 0 && ids.every(id => id > 0 && Boolean(report.references?.[id - 1]));
    return <React.Fragment key={index}>{resolvable ? walkClaimText(part, report.references) : part}</React.Fragment>;
  });
}

const distinctNotes = (values: Array<string | undefined>): string[] =>
  [...new Set(values.filter((value): value is string => Boolean(value?.trim())))];

type ReportWithSectionQuarantine = SiteRagReport & {
  sectionQuarantine?: Record<string, unknown>;
};

export const ProjectIntentOverviewSection: React.FC<ProjectIntentOverviewSectionProps> = ({
  report,
  theme,
  reportSite,
  reportSiteLayers,
  propertyMapSurfaceMode,
  onEditLayers,
  isAdmin,
  onSectionRegen,
  isEditing = false,
  headingOverrides,
  onHeadingChange,
  sectionDefaultHeading,
  sectionStyle,
  sectionPageColumnProps,
  shellPadding,
  omitSectionSidebar,
  extraClassName,
  exportMode = false,
  presetId,
  editorialActive = false,
  editorialSectionNumber,
}) => {
  const looseReport = report as LooseOverviewReport;
  const overview = resolveOverviewProse(report);
  const overviewParas = overview ? overviewParagraphs(overview) : [];
  const quarantineMap = (report as ReportWithSectionQuarantine).sectionQuarantine;
  const quarantined = quarantineMap?.projectIntentOverview != null || quarantineMap?.keyFindings != null;
  const placeholderCopy = quarantined
    ? 'This section was flagged for review by an upstream quality check. See the quarantine banner above for details.'
    : !report.projectIntentAtGen
      ? 'Set Project Intent in site configuration, then rerun the report to populate this overview.'
      : 'Project intent overview was not generated for this report. Run a full rerun to regenerate this section.';

  const showExecSummaryFindings = report.reportType === 'executive-summary';
  const specRailItems = resolveIntentSpecRail(report);
  const effectiveIntent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  const screeningMode = resolveReportScreeningMode(report, effectiveIntent);
  const savedRecs = deriveScreeningRecommendationsForPresentation(report, report.siteId);
  const savedSet = derivePresentedScreeningRecommendationSetV1(report, report.siteId);
  const dcPowerDiag = savedSet.questions['dc-power'];
  const hasSavedRecs = Object.keys(savedRecs).length > 0;
  const legacyCapacity = presentScreeningRecommendationFields(report, [looseReport.gridAnalysis?.availableCapacity ?? ''])?.[0];
  const legacyPower = presentScreeningRecommendationFields(report, [looseReport.btmViability?.recommendedPath ?? '', looseReport.btmViability?.summary ?? '']);
  const legacyCooling = presentScreeningRecommendationFields(report, [looseReport.coolingAnalysis?.recommendedSystem ?? '', looseReport.coolingAnalysis?.waterAvailability ?? '']);
  // These are immutable saved outcomes and qualifications, not an accepted
  // architecture. A long note gets its own reading width; it is never clamped.
  const recommendationRows = [
    {
      id: 'capacity', label: 'Supportable capacity range',
      value: savedRecs['dc-power']?.value
        ?? (legacyCapacity
          ? `Grid POI evidence: ${legacyCapacity} (sizing range pending study)`
          : 'Pending utility & buildable-envelope confirmation'),
      notes: distinctNotes([
        savedRecs['dc-power']?.basisNote,
        dcPowerDiag.status === 'unresolved' ? dcPowerDiag.unresolvedReason : undefined,
      ]),
    },
    {
      id: 'power', label: 'Recommended power & BTM path',
      value: distinctNotes([savedRecs['btm-architecture']?.value, savedRecs['power-type']?.value]).join('\n\n')
        || legacyPower?.[0]
        || 'No saved power recommendation is available',
      notes: distinctNotes([savedRecs['btm-architecture']?.basisNote, savedRecs['power-type']?.basisNote,
        !savedRecs['btm-architecture'] && !savedRecs['power-type'] ? legacyPower?.[1] : undefined]),
    },
    {
      id: 'cooling', label: 'Recommended cooling & heat rejection',
      value: distinctNotes([savedRecs['cooling-strategy']?.value, savedRecs['cooling-type']?.value]).join(' · ')
        || legacyCooling?.[0]
        || 'No saved cooling recommendation is available',
      // Rack-loop and cold-end conditions can differ. Keep both, even when
      // both recommendations exist; nullish precedence used to drop the latter.
      notes: distinctNotes([savedRecs['cooling-strategy']?.basisNote, savedRecs['cooling-type']?.basisNote,
        !savedRecs['cooling-strategy'] && !savedRecs['cooling-type'] ? legacyCooling?.[1] : undefined]),
    },
  ];
  const powerRecommendation = recommendationRows[1]!;
  const supportingRecommendations = [recommendationRows[0]!, recommendationRows[2]!];
  const rationaleRows = [powerRecommendation, ...supportingRecommendations].filter(row => row.notes.length > 0);
  // The compact brief reads the frozen structured intent only. Do not extract
  // MW figures from rationale: load, nameplate, standby duty and model inputs
  // have different meanings, even when the saved prose mentions all of them.
  const recordedBriefRows = specRailItems.filter(row => typeof row.key === 'string'
    && !['Screening Mode', 'Recommended Load Range', 'Recommended Cooling', 'Recommended Power Path'].includes(row.key));
  const recommendationTypography = reportTypographySizes(theme);
  const recommendationBodyStyle: React.CSSProperties = {
    fontFamily: 'var(--report-body-font, inherit)',
    fontSize: recommendationTypography.body,
    color: roleColor(theme, 'body'),
  };
  const proposalRows = screeningMode === 'evaluate-proposal'
    ? buildProposalAlignmentRows(report, effectiveIntent)
    : [];
  const showModeScorecard =
    effectiveIntent?.screeningMode === 'recommend'
    || effectiveIntent?.screeningMode === 'evaluate-proposal'
    || (screeningMode === 'recommend' && hasSavedRecs)
    || (screeningMode === 'evaluate-proposal' && proposalRows.length > 0 && Boolean(
      (looseReport.scores && looseReport.scores.length > 0)
      || looseReport.gridAnalysis
      || looseReport.coolingAnalysis
      || looseReport.btmViability,
    ));

  // WAVE 3 — §01 prose-led monograph. When a GENERATED editorial layer exists,
  // the flowing EditorialProseBody (drop-cap + pull-quote) re-voices the brief,
  // and the existing `overviewParas` prose is SUPPRESSED so it is not doubled.
  // The "At a glance" spec rail + the site map are EXHIBITS and always render.
  // No layer ⇒ byte-identical (the reportSurfaceParity fixtures carry none).
  const layerPresent = hasGeneratedEditorialLayer(report, 'projectIntentOverview');

  // N1.5C — the heading node is SHARED between the two shell modes so the
  // TOC anchor / edit wiring are identical on both paths. In editorial mode the
  // shell owns the opener <h2>; in classic mode the caller renders it below.
  const headingNode = (
    <EditableHeading
      headingKey="projectIntentOverview"
      defaultText={sectionDefaultHeading('projectIntentOverview', 'Project Intent & Site Overview')}
      className={REPORT_SECTION_HEADING_TEXT_CLASS}
      isEditing={isEditing}
      overrides={headingOverrides ?? {}}
      onChange={onHeadingChange ?? (() => {})}
    />
  );

  return (
    <EditorialSectionShell
      id="projectIntentOverview"
      data-section="projectIntentOverview"
      data-section-id="projectIntentOverview"
      data-executive-project-overview={showExecSummaryFindings ? 'true' : undefined}
      style={sectionStyle}
      className="space-y-4"
      shellPadding={shellPadding}
      omitSectionSidebar={omitSectionSidebar}
      theme={theme}
      extraClassName={extraClassName}
      {...sectionPageColumnProps}
      editorialActive={editorialActive}
      sectionNumber={editorialSectionNumber}
      heading={headingNode}
    >
      {/* Classic mode (flag OFF) — the caller-owned head renders byte-identically
           to the retired ClassicSectionShell path. Editorial mode (flag ON): the
           shell owns the opener (hero numeral + numbered head), so this is
           suppressed. */}
      {!editorialActive && (
        <header className="space-y-3">
          {/* P2.4 — heading colour/font routed through the canonical helper so
               a theme preset reskins it. This variant keeps its own `m-0 pb-0`
               (no bottom rule) className; reportSectionHeadingStyle only adds a
               `borderColor` which is a no-op without a border width, so the
               legacy render is byte-identical. */}
          <h2
            className={`${REPORT_SECTION_HEADING_TEXT_CLASS} m-0 pb-0`}
            style={reportSectionHeadingStyle(theme)}
            data-heading="true"
          >
            {headingNode}
          </h2>
        </header>
      )}

      <SectionQuarantineBanner
        sectionId="projectIntentOverview"
        sectionLabel="Project Intent & Site Overview"
        guardStatus={report.sectionGuardStatus?.projectIntentOverview}
        quarantinedOriginal={report.quarantinedOriginal?.projectIntentOverview}
        renderedText={overview ?? report.projectIntentOverview}
        onRerun={onSectionRegen ? () => onSectionRegen('projectIntentOverview') : undefined}
      />

      {!exportMode &&
        reportSite?.projectIntent?.screeningMode &&
        report.projectIntentAtGen?.screeningMode &&
        reportSite.projectIntent.screeningMode !== report.projectIntentAtGen.screeningMode && (
          <div
            data-testid="project-intent-screening-mode-drift"
            role="status"
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-950 dark:text-amber-200 print:hidden"
          >
            <strong>Intent drift:</strong> This frozen report was generated in{' '}
            <strong>
              {report.projectIntentAtGen.screeningMode === 'evaluate-proposal'
                ? 'Evaluate my proposal (evaluate-proposal)'
                : 'Recommend an approach (recommend)'}
            </strong>{' '}
            mode, while live Project Intent is set to{' '}
            <strong>
              {reportSite.projectIntent.screeningMode === 'evaluate-proposal'
                ? 'Evaluate my proposal (evaluate-proposal)'
                : 'Recommend an approach (recommend)'}
            </strong>
            . Rerun Screening Assessment to align the analysis and PDF export with the current Project Intent.
          </div>
        )}

      {/* SECTION-PORT (3B, "brief + spec rail") — the locked Intent layout
           leads with an "at a glance" spec rail that summarises the gen-time
           project-intent snapshot + site geometry, composed BENEATH the
           head/banner and ABOVE the existing prose+map brief (all of which is
           preserved untouched). The rail is theme-token-driven (renders across
           journal/institutional/monograph), and each entry is dropped when its
           source value is absent, so a thin report shows a shorter ledger — no
           fabricated specs.
           J5 — rendered as a COMPACT dotted-leader EditorialDatasheet (single
           headless group: key … value per row) rather than the taller stacked
           EditorialDefinitionList, so the Overview map + brief fit on ONE page.
           GAP: none material — the parcel-boundary figure is already provided
           by the existing PropertyMapContent block below (boundary geometry),
           so the MAP's 3B SVG exhibit is NOT duplicated here. */}
      {specRailItems.length > 0 && !(showModeScorecard && screeningMode === 'recommend') && (
        <div
          data-testid="project-intent-spec-rail"
          style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          <div
            style={{
              fontFamily: 'var(--report-body-font, inherit)',
              fontSize: REPORT_TEXT_PX.label,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: roleColor(theme, 'body'),
              opacity: 0.7,
              marginBottom: 4,
              breakAfter: 'avoid',
              pageBreakAfter: 'avoid',
            }}
          >
            At a glance
          </div>
          <EditorialDatasheet theme={theme} groups={[{ rows: specRailItems }]} />
        </div>
      )}

      {showModeScorecard && (
        <div
          data-testid="screening-mode-scorecard"
          data-screening-mode={screeningMode}
          className={`min-w-0 rounded-md border print:shadow-none ${screeningMode === 'recommend' ? 'space-y-5 p-4 sm:p-5 print:p-0 print:border-0' : 'p-3.5 space-y-2.5 break-inside-avoid'}`}
          style={{ color: roleColor(theme, 'body'), borderColor: roleColor(theme, 'hair') }}
        >
          {screeningMode === 'recommend' ? (
            <>
              <header className="space-y-2 [break-after:avoid]">
                <h3 className="m-0 font-semibold leading-snug" style={{ fontFamily: theme.headingFont,
                  fontSize: recommendationTypography.h3, color: roleColor(theme, 'ink') }}>
                  Recommended Site Architecture &amp; Supportable Envelope
                </h3>
                <p className="m-0 leading-relaxed" style={recommendationBodyStyle}>
                  Power and cooling from this report’s saved screening outcome.
                </p>
              </header>
              <p className="m-0 border-l-2 px-3 py-2 leading-relaxed" style={{ ...recommendationBodyStyle,
                borderColor: roleColor(theme, 'accent'), backgroundColor: roleColor(theme, 'accentSoft') }}>
                <strong>Intent: Recommend an approach.</strong>{' '}
                Review this screening option before adopting it. Recorded brief inputs remain separate.
              </p>
              <dl data-testid="screening-power-summary" className="min-w-0 space-y-2">
                <dt style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>Saved power option</dt>
                <dd className="m-0 min-w-0">
                  <SavedScreeningProse text={powerRecommendation.value} className="font-semibold leading-snug"
                    style={{ fontFamily: theme.headingFont, fontSize: recommendationTypography.h3, color: roleColor(theme, 'ink') }}
                    renderPlainText={value => renderSavedRecommendationText(value, report)} />
                </dd>
              </dl>
              <dl data-testid="screening-supporting-facts" className="flex min-w-0 flex-wrap gap-x-6 border-y py-4 print:block"
                style={{ borderColor: roleColor(theme, 'hair') }}>
                {supportingRecommendations.map(row => (
                  <div key={row.id} className="min-w-0 flex-1 basis-56 space-y-2 py-2 print:mb-3">
                    <dt className="[break-after:avoid]" style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>
                      {row.label}
                    </dt>
                    <dd className="m-0 min-w-0">
                      <SavedScreeningProse text={row.value} className="font-semibold leading-relaxed" style={recommendationBodyStyle}
                        renderPlainText={value => renderSavedRecommendationText(value, report)} />
                    </dd>
                  </div>
                ))}
              </dl>
              <section data-testid="screening-recorded-brief" className="min-w-0 space-y-3">
                <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Recorded brief constraints</h4>
                {recordedBriefRows.length > 0 ? (
                  <div data-testid="project-intent-spec-rail">
                    <EditorialDatasheet theme={theme} groups={[{ rows: recordedBriefRows }]} />
                  </div>
                ) : <p className="m-0 leading-relaxed" style={recommendationBodyStyle}>No fixed architecture constraints were recorded in the saved brief.</p>}
                <p className="m-0 leading-relaxed" style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>
                  These are saved project inputs, not a recommended size range or an accepted architecture.
                </p>
              </section>
              {rationaleRows.length > 0 && (
                <section data-testid="screening-recommendation-rationale" className="min-w-0 space-y-4 border-t pt-4"
                  style={{ borderColor: roleColor(theme, 'hair') }}>
                  <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Saved rationale and conditions</h4>
                  {rationaleRows.map(row => (
                    <div key={row.id} data-testid="screening-recommend-row" data-recommendation={row.id}
                      className="min-w-0 space-y-2 [break-inside:auto]">
                      {rationaleRows.length > 1 && <h5 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>{row.label}</h5>}
                      {row.notes.map(note => (
                        <SavedScreeningProse key={note} text={note} className="leading-relaxed [orphans:3] [widows:3]"
                          style={recommendationBodyStyle} renderPlainText={value => renderSavedRecommendationText(value, report)} />
                      ))}
                    </div>
                  ))}
                </section>
              )}
              <p className="m-0 border-t pt-3 leading-relaxed" style={{ ...recommendationBodyStyle,
                color: roleColor(theme, 'muted'), borderColor: roleColor(theme, 'hair') }}>
                Recommendations remain advisory until adopted into Project Intent and verified in Stage 2.
              </p>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-900 border border-indigo-500/30">
                    Evaluate My Proposal
                  </span>
                  <span className="text-xs font-semibold">
                    Proposal vs. Verified Site Evidence Scorecard
                  </span>
                </div>
                <span className="text-[11px] opacity-75">
                  Stress-testing declared targets against site constraints
                </span>
              </div>
              {proposalRows.length > 0 && (
                <div className="overflow-x-auto pt-1">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-border-subtle/70 text-[10px] uppercase tracking-wider opacity-75">
                        <th className="py-1.5 pr-3 font-semibold">Requirement</th>
                        <th className="py-1.5 pr-3 font-semibold">Declared Proposal</th>
                        <th className="py-1.5 pr-3 font-semibold">Verified Site Evidence</th>
                        <th className="py-1.5 text-right font-semibold">Alignment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle/40">
                      {proposalRows.map((row) => (
                        <tr key={row.dimension} data-testid="screening-proposal-row">
                          <td className="py-1.5 pr-3 font-semibold align-top whitespace-nowrap">
                            {row.dimension}
                          </td>
                          <td className="py-1.5 pr-3 align-top">
                            {row.declaredProposal}
                          </td>
                          <td className="py-1.5 pr-3 align-top opacity-90">
                            {row.verifiedEvidence}
                          </td>
                          <td className="py-1.5 text-right align-top whitespace-nowrap">
                            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${row.badge.className}`}>
                              {row.badge.label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div
        // DEFECT 1c — FLATTEN TO CREAM: strip the grey `bg-surface-1` card
        // fill, hairline border, rounding + shadow so the prose + map sit BARE
        // on the cream page. `break-inside-avoid` is retained (HR-QA fix
        // 2026-06-10 — the prose + map + legend never split mid-block across
        // PDF pages; ignored by CSS fragmentation when it exceeds a full page).
        className="break-inside-avoid print:shadow-none"
        data-testid="project-intent-overview-panel"
      >
        {/* P2.2 — responsive grid (prose | map) instead of a lg:float-right
             map. The map cell self-starts at the top so it pins to the
             section head while the prose flows in its own 1fr column. This
             kills the L-shaped wasted gap that the old float left under short
             prose (and never orphans the map on long prose, since the columns
             are independent). Below lg, stack with the map after the prose
             (flex-col-reverse keeps the prior mobile order). The data passed
             to PropertyMapContent is unchanged. */}
        {/* DEFECT 7 — tighter gutters (no card padding) so the overview fits ONE
            A4 page: heading + narrative + map + caption. */}
        <div className="flex flex-col-reverse gap-4 lg:gap-0 lg:grid lg:grid-cols-[1fr_23rem]">
          <div
            // DEFECT 1c — strip the grey `bg-surface-2/50` mobile fill + border;
            // the map block flows bare on cream. (The lg: breakpoint already
            // dropped the fill — this also clears the mobile/print wash.)
            className="mb-0 mt-4 lg:mt-0 lg:mb-0 lg:self-start lg:pl-6 p-0 lg:order-2"
            data-testid="property-map-block"
            data-surface-mode={propertyMapSurfaceMode}
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2 lg:mb-3">
              Site overview
            </div>
            <PropertyMapContent
              report={report}
              reportSite={reportSite}
              reportSiteLayers={reportSiteLayers}
              surfaceMode={propertyMapSurfaceMode}
              onEditLayers={onEditLayers}
              layout="projectIntentOverview"
              theme={theme}
              presetId={presetId}
              editorialActive={editorialActive}
            />
          </div>

          {/* William QA (2026-06-17) — force the WCAG-clamped dark body ink
              (roleColor 'body') when themed so the overview prose reads BLACK on
              the light report (theme-appropriate dark in any theme), matching
              every other section. Without it the <p> children inherited the muted
              slate bodyTextColor and rendered near-invisible. */}
          <div
            className="text-sm leading-relaxed text-text-primary space-y-3 lg:order-1 lg:min-w-0"
            style={theme ? { color: roleColor(theme, 'body') } : undefined}
            data-testid="project-intent-overview-content"
          >
            {/* WAVE 3 — flowing editorial prose body when a generated layer is
                present (re-voices the brief); the structured `overviewParas`
                prose is suppressed in tandem so it is not doubled. */}
            {layerPresent ? (
              <EditorialProseBody report={report} sectionId="projectIntentOverview" theme={theme} />
            ) : overviewParas.length > 0 ? (
              overviewParas.map((para, i) => (
                <p key={i} className="m-0 whitespace-pre-wrap break-words">
                  {walkClaimsText(para, report.references, report.claims)}
                </p>
              ))
            ) : (
              <KeyFindingsEmptyState
                message={placeholderCopy}
                isAdmin={isAdmin}
                onRegenerate={onSectionRegen}
                exportMode={exportMode}
              />
            )}
          </div>
        </div>
      </div>

      {showExecSummaryFindings && (
        <ContextualKeyFindingsBlock
          report={report}
          theme={theme}
          className="mt-6"
        />
      )}
    </EditorialSectionShell>
  );
};
